/**
 * Resolves raw cart items (stored in localStorage/state) against Gatsby product data.
 * Correctly computes variant pricing, discounts, and item totals across all product types.
 *
 * @param {Array} rawCart - Array of cart objects { productId, size, thick, selectedSize, color, type, quantity }
 * @param {Array} allProducts - Array of GraphQL product nodes
 * @returns {{ items: Array, total: number, itemCount: number }}
 */
export function resolveCartItems(rawCart = [], allProducts = []) {
  if (!Array.isArray(rawCart) || !Array.isArray(allProducts)) {
    return { items: [], total: 0, itemCount: 0 };
  }

  let total = 0;
  let itemCount = 0;

  const items = rawCart
    .map((cartItem) => {
      const product = allProducts.find((p) => p?.meta?.id === cartItem?.productId);
      if (!product) return null;

      let basePrice = 0;
      let offerPrice = 0;

      // 1. Mattresses, Futons, Bedspreads, Protectors (have priceList)
      if (Array.isArray(product.priceList) && product.priceList.length > 0) {
        // Find matching size
        let sizeMatch = null;
        if (cartItem.selectedSize) {
          const selectedSizeStr = JSON.stringify(cartItem.selectedSize);
          sizeMatch = product.priceList.find(
            (s) => JSON.stringify(s.size) === selectedSizeStr
          );
        }
        if (!sizeMatch) {
          sizeMatch = product.priceList[0];
        }

        // Find matching thickness variant
        let variant = null;
        if (Array.isArray(sizeMatch.varients)) {
          const targetThick = String(cartItem.thick || '0');
          variant = sizeMatch.varients.find((v) => String(v.t) === targetThick);
          if (!variant) {
            variant = sizeMatch.varients[0];
          }
        }

        basePrice = variant?.p || product.price || 0;
        const discount = product.offer || 0;
        offerPrice = discount > 0 ? Math.floor(basePrice - basePrice * (discount / 100)) : basePrice;
      }
      // 2. Pillows & products with types array
      else if (Array.isArray(product.types) && product.types.length > 0) {
        const typeIndex = parseInt(cartItem.type, 10) || 0;
        const selectedType = product.types[typeIndex] || product.types[0];

        basePrice = selectedType.p || product.price || 0;
        if (selectedType.s) {
          offerPrice = selectedType.s;
        } else {
          const discount = product.offer || 0;
          offerPrice = discount > 0 ? Math.floor(basePrice - basePrice * (discount / 100)) : basePrice;
        }
      }
      // 3. Simple products (Eye Masks, etc.)
      else {
        basePrice = product.price || 0;
        const discount = product.offer || 0;
        offerPrice = discount > 0 ? Math.floor(basePrice - basePrice * (discount / 100)) : basePrice;
      }

      const qty = Math.max(1, parseInt(cartItem.quantity, 10) || 1);
      const itemPrice = parseFloat(offerPrice.toFixed(2));
      const lineTotal = itemPrice * qty;

      total += lineTotal;
      itemCount += qty;

      return {
        ...cartItem,
        product,
        basePrice: parseFloat(basePrice.toFixed(2)),
        price: itemPrice.toFixed(2),
        lineTotal: lineTotal.toFixed(2),
        quantity: qty,
      };
    })
    .filter(Boolean);

  return {
    items,
    total: parseFloat(total.toFixed(2)),
    itemCount,
  };
}
